import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Trash2, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const formatDate = (isoString) => {
  const date = new Date(isoString);
  return date.toLocaleDateString('en-US', { 
    month: 'short', 
    day: 'numeric',
    year: date.getFullYear() !== new Date().getFullYear() ? 'numeric' : undefined
  });
};

export default function HistoryPage() {
  const navigate = useNavigate();
  const [history, setHistory] = useState([]);

  useEffect(() => {
    const saved = localStorage.getItem("decisora_history");
    if (saved) {
      try {
        setHistory(JSON.parse(saved));
      } catch (e) {
        console.error("Failed to load history");
      }
    }
  }, []);

  const handleDelete = (sessionId) => {
    const updated = history.filter(h => h.session_id !== sessionId);
    setHistory(updated);
    localStorage.setItem("decisora_history", JSON.stringify(updated));
    toast.success("Removed from history");
  };

  const handleClearAll = () => {
    setHistory([]);
    localStorage.removeItem("decisora_history");
    toast.success("History cleared");
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-background/80 backdrop-blur-xl border-b border-border/50">
        <div className="max-w-3xl mx-auto px-6 h-14 flex items-center justify-between">
          <button
            data-testid="back-btn"
            onClick={() => navigate("/")}
            className="text-muted-foreground hover:text-foreground transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
          </button>
          {history.length > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleClearAll}
              className="text-xs text-muted-foreground hover:text-destructive"
            >
              Clear all
            </Button>
          )}
        </div>
      </header>

      {/* Content */}
      <main className="max-w-3xl mx-auto px-6 py-10">
        <div className="space-y-8">
          <div className="space-y-3">
            <p className="text-xs font-medium text-primary uppercase tracking-wider">
              Decision History
            </p>
            <h1 className="font-heading text-2xl md:text-3xl text-foreground">
              Past decisions
            </h1>
            <p className="text-muted-foreground">
              Your recent decision analyses, stored locally in your browser.
            </p>
          </div>

          {history.length === 0 ? (
            <div className="py-16 text-center">
              <p className="text-muted-foreground mb-4">No decisions yet</p>
              <Button
                onClick={() => navigate("/")}
                variant="outline"
                className="h-10 px-6 rounded-full text-sm"
              >
                Start a new decision
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              {history.map((item, index) => (
                <motion.div
                  key={item.session_id}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.03 }}
                  className="floating-card p-5"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <p className="text-foreground font-medium mb-2 line-clamp-2">
                        {item.decision_text}
                      </p>
                      <div className="flex items-center gap-3 text-xs text-muted-foreground">
                        <span>{item.framework}</span>
                        {item.confidence_score && (
                          <>
                            <span className="text-border">•</span>
                            <span>{item.confidence_score}% confidence</span>
                          </>
                        )}
                        <span className="text-border">•</span>
                        <span>{formatDate(item.timestamp)}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => navigate(`/map/${item.session_id}`)}
                        className="h-8 px-2 text-muted-foreground hover:text-foreground"
                      >
                        <ExternalLink className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(item.session_id)}
                        className="h-8 px-2 text-muted-foreground hover:text-destructive"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-border/30 py-8 px-6">
        <div className="max-w-3xl mx-auto text-center space-y-3">
          <p className="text-xs text-muted-foreground">
            History is stored locally in your browser. Clearing browser data will remove it.
          </p>
          <p className="text-[11px] text-muted-foreground/50">
            Decisora provides structured thinking tools, not professional advice.
          </p>
        </div>
      </footer>
    </div>
  );
}
